import React, { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useLang } from "../Utility/Languagecontext";
import { PhoneField, getCountry } from "../Utility/Phonefield";
import { COMPANY, whatsappUrl } from "../Data/Company";
import { INTERESTS_EN, submitLead } from "../Utility/leads";

// ============ فيديو الخلفية ============
// حط الفيديو في Assets وغيّر الاسم هنا. (بدون صوت، يفضل MP4 H.264 صغير الحجم)
import contactVideo from "../Assets/palm-web.mp4";
// صورة تظهر قبل ما الفيديو يحمّل (اختياري). سيبها null لو مفيش.
const CONTACT_POSTER = null;

// بيانات التواصل بقت في src/Data/Company.js (مصدر واحد للموقع كله)
const EASE = [0.76, 0, 0.24, 1];

// سطر العنوان بيطلع من ورا قناع
const MaskLine = ({ children, delay = 0, show }) => (
  <span className="mask">
    <motion.span
      className="mask__inner"
      initial={false}
      animate={{ y: show ? 0 : "115%" }}
      transition={{ duration: 1.1, delay: show ? delay : 0, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Arrow = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
    <path d="M4 12h16M14 6l6 6-6 6" />
  </svg>
);

export const MainContactUs = () => {
  const { t, lang } = useLang();
  const c = t.contact;
  const intro = t.contactPage.msgIntro;

  const headRef = useRef(null);
  const headIn = useInView(headRef, { amount: 0.3 });

  // ---- الفيديو: حركة بارالاكس خفيفة + تشغيل/إيقاف حسب الظهور ----
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const reduce = useReducedMotion();
  const visible = useInView(sectionRef, { amount: 0 });
  // الفيديو مبيتحمّلش غير لما القسم يقرّب من الشاشة (الصفحة بتفتح أسرع)
  const near = useInView(sectionRef, { once: true, margin: "600px 0px 600px 0px" });
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const vy = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (visible && !reduce) {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      v.pause();
    }
  }, [visible, reduce, near]);

  const [form, setForm] = useState({ name: "", country: "ae", phone: "", email: "", interest: 0, message: "" });
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // الفورم بيفتح واتساب برسالة جاهزة (مفيش باك إند مطلوب)
  const onSubmit = (e) => {
    e.preventDefault();
    const text = [
      intro,
      `${c.form.name}: ${form.name}`,
      `${c.form.phone}: +${getCountry(form.country)[3]} ${form.phone.trim()}`,
      form.email && `${c.form.email}: ${form.email}`,
      `${c.form.interest}: ${c.interests[form.interest]}`,
      form.message && `${c.form.message}: ${form.message}`,
    ]
      .filter(Boolean)
      .join("\n");
    // الليد بيتحفظ في الداشبورد (في الخلفية) وبعدها واتساب بيفتح زي الأول
    submitLead({
      name: form.name,
      email: form.email,
      phoneCode: `+${getCountry(form.country)[3]}`,
      phone: form.phone,
      country: form.country,
      message: form.message,
      interest: INTERESTS_EN[form.interest],
      source: "home-contact",
    });
    window.open(whatsappUrl(text), "_blank", "noopener");
    setSent(true);
  };

  const info = [
    { label: c.info.phone, value: COMPANY.phoneLabel, href: `tel:${COMPANY.phone}`, ltr: true },
    { label: c.info.email, value: COMPANY.email, href: `mailto:${COMPANY.email}`, ltr: true },
    { label: c.info.address, value: c.info.addressValue, href: COMPANY.mapShareUrl },
    { label: c.info.hours, value: `${c.info.hoursValue} · ${c.info.closed}` },
  ];

  return (
    <section className="contact" id="contact" ref={sectionRef}>
      {/* ====== خلفية الفيديو ====== */}
      <div className="contact__bg" aria-hidden="true">
        <motion.div className="contact__bg-move" style={reduce ? undefined : { y: vy }}>
          <video
            ref={videoRef}
            className="contact__video"
            src={near && !reduce ? contactVideo : undefined}
            poster={CONTACT_POSTER || undefined}
            muted
            loop
            playsInline
            preload="none"
            disablePictureInPicture
            tabIndex={-1}
          />
        </motion.div>
        <div className="contact__bg-shade" />
      </div>

      <div className="contact__grid">
        {/* ====== الجانب الأول: العنوان + بيانات التواصل ====== */}
        <div className="contact__side">
          <div ref={headRef}>
            <p className="contact__label">{c.label}</p>
            <h2 className="contact__title">
              {c.title.map((line, i) => (
                <MaskLine key={line} delay={i * 0.14} show={headIn}>
                  {line}
                </MaskLine>
              ))}
            </h2>
            <p className="contact__sub">{c.sub}</p>
          </div>

          <motion.ul
            className="contact__info"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: EASE }}
          >
            {info.map((it) => (
              <li key={it.label} className="cinfo">
                <span className="cinfo__label">{it.label}</span>
                {it.href ? (
                  <a
                    className={`cinfo__value ${it.ltr ? "is-ltr" : ""}`}
                    href={it.href}
                    {...(it.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    {it.value}
                    <Arrow />
                  </a>
                ) : (
                  <span className="cinfo__value">{it.value}</span>
                )}
              </li>
            ))}
          </motion.ul>
        </div>

        {/* ====== الجانب التاني: الفورم داخل قوس ====== */}
        <motion.div
          className="contact__panel"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.1, ease: EASE }}
        >
          <i className="contact__arch" aria-hidden="true" />

          {sent ? (
            <div className="cform__done" role="status">
              <span className="cform__done-mark">✦</span>
              <h3>{c.done.title}</h3>
              <p>{c.done.text}</p>
              <button type="button" className="btn btn--ghost" onClick={() => setSent(false)}>
                {c.done.again}
              </button>
            </div>
          ) : (
            <form className="cform" onSubmit={onSubmit}>
              <fieldset className="cform__chips">
                <legend>{c.form.interest}</legend>
                {c.interests.map((label, i) => (
                  <button
                    key={label}
                    type="button"
                    className={`cchip ${form.interest === i ? "is-on" : ""}`}
                    aria-pressed={form.interest === i}
                    onClick={() => setForm((f) => ({ ...f, interest: i }))}
                  >
                    {label}
                  </button>
                ))}
              </fieldset>

              <div className="cform__row">
                <label className="cfield">
                  <span>{c.form.name}</span>
                  <input type="text" required autoComplete="name" value={form.name} onChange={set("name")} />
                </label>
                <PhoneField
                  label={c.form.phone}
                  searchLabel={c.form.search}
                  noResults={c.form.noResults}
                  lang={lang}
                  country={form.country}
                  number={form.phone}
                  onCountry={(iso) => setForm((f) => ({ ...f, country: iso }))}
                  onNumber={(v) => setForm((f) => ({ ...f, phone: v }))}
                />
              </div>

              <label className="cfield">
                <span>{c.form.email}</span>
                <input type="email" dir="ltr" autoComplete="email" value={form.email} onChange={set("email")} />
              </label>

              <label className="cfield">
                <span>{c.form.message}</span>
                <textarea rows={3} value={form.message} onChange={set("message")} />
              </label>

              <div className="cform__foot">
                <p>{c.form.note}</p>
                <button type="submit" className="btn btn--solid">
                  {c.form.submit}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};